import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useAppSelector } from "../../redux/hooks";
import type { RootState } from "../../redux/store";
import { usePermissionAccess } from "../../hooks/usePermissionAccess";
import { BACKEND_MODULES } from "../../constants/permissionModuleActions";
import { getAgentStatusApi, getToolbarConfigApi } from "../../Apis/telephony";
import type {
  AgentStatusData,
  ToolbarConfigData,
} from "../../Interfaces/TelephonyInterface";
import { colorTokens, getSpacing, getTypography } from "../../designTokens";
import { AMEYO_EXPAND_TOOLBAR_EVENT } from "./ameyoToolbarEvents";
import { useSessionKeepAlive } from "../../auth/hooks/useSessionKeepAlive";

const STATUS_POLL_MS = 30_000;
const MINIMAL_WIDTH_PX = 360;
const MINIMAL_HEIGHT_PX = 460;
const LARGE_MAX_WIDTH_PX = 1100;
/** Compact vertical polished logo pill (slim right-edge handle). */
const LAUNCHER_WIDTH_PX = 16;
const LAUNCHER_PILL_RADIUS = "999px";
/** Wispr Flow–style waveform line widths (px), top → bottom. */
const LAUNCHER_WAVE_WIDTHS_PX = [5, 9, 6, 8] as const;

type LoadState = "idle" | "loading" | "ready" | "error" | "hidden";
type PanelSizeMode = "minimal" | "large";

/**
 * Global Ameyo agent toolbar — slim right-center floating widget (chat-style).
 * Expand opens a larger centered popup. Mounted at app shell so it persists across
 * My Leads, Overview, and Lead Details (those routes are outside DashboardShell).
 */
const AmeyoCallingPanel: React.FC = () => {
  const accessToken = useAppSelector(
    (state: RootState) => state.auth.accessToken
  );
  const { modulePermissions, resolveModuleAccess } = usePermissionAccess();
  const hasOutboundCalling =
    modulePermissions?.[BACKEND_MODULES.OUTBOUND_CALLING]?.enabled === true;
  const canOutboundCalling = resolveModuleAccess(hasOutboundCalling);

  const [loadState, setLoadState] = useState<LoadState>("idle");
  const [config, setConfig] = useState<ToolbarConfigData | null>(null);
  const [agentStatus, setAgentStatus] = useState<AgentStatusData | null>(null);
  const [open, setOpen] = useState(false);
  const [sizeMode, setSizeMode] = useState<PanelSizeMode>("minimal");
  const [configError, setConfigError] = useState<string | null>(null);
  /** Keep iframe mounted after first open so Ameyo login survives minimize. */
  const [iframeMounted, setIframeMounted] = useState(false);
  const [launcherTooltipOpen, setLauncherTooltipOpen] = useState(false);
  useSessionKeepAlive(open);

  const fetchConfig = useCallback(async () => {
    setLoadState("loading");
    setConfigError(null);
    try {
      const res = await getToolbarConfigApi();
      const data = res.data;
      if (!data?.enabled) {
        setConfig(data ?? { enabled: false });
        setLoadState("hidden");
        return;
      }
      setConfig(data);
      setLoadState("ready");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load calling toolbar";
      setConfigError(message);
      setLoadState("error");
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await getAgentStatusApi();
      setAgentStatus(res.data ?? null);
    } catch {
      // Non-blocking — status chip may stay stale
    }
  }, []);

  useEffect(() => {
    if (!accessToken || !canOutboundCalling) {
      setLoadState("hidden");
      setConfig(null);
      return;
    }
    void fetchConfig();
  }, [accessToken, canOutboundCalling, fetchConfig]);

  useEffect(() => {
    if (loadState !== "ready" || !config?.enabled || !config.agentProfile) {
      return;
    }
    void fetchStatus();
    const id = window.setInterval(() => {
      void fetchStatus();
    }, STATUS_POLL_MS);
    return () => window.clearInterval(id);
  }, [loadState, config?.enabled, config?.agentProfile, fetchStatus]);

  useEffect(() => {
    const onExpand = () => {
      setOpen(true);
      setIframeMounted(true);
      setSizeMode("minimal");
      void fetchStatus();
    };
    window.addEventListener(AMEYO_EXPAND_TOOLBAR_EVENT, onExpand);
    return () =>
      window.removeEventListener(AMEYO_EXPAND_TOOLBAR_EVENT, onExpand);
  }, [fetchStatus]);

  useEffect(() => {
    if (!open) {
      setSizeMode("minimal");
    }
  }, [open]);

  const openPanel = useCallback(() => {
    setOpen(true);
    setIframeMounted(true);
    void fetchStatus();
  }, [fetchStatus]);

  const minimizePanel = useCallback(() => {
    setOpen(false);
    setSizeMode("minimal");
  }, []);

  if (!accessToken || !canOutboundCalling) {
    return null;
  }

  if (loadState === "hidden" || loadState === "idle") {
    return null;
  }

  if (loadState === "loading") {
    return (
      <div
        style={{
          position: "fixed",
          right: getSpacing("4px"),
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 1100,
          ...getTypography("Body/Small"),
          color: colorTokens["Text/Light Grey"],
          backgroundColor: colorTokens["Background/White"],
          border: `1px solid ${colorTokens["Borders/Light"]}`,
          borderRadius: getSpacing("8px"),
          padding: `${getSpacing("8px")} ${getSpacing("12px")}`,
          boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
        }}
        id="ameyo-calling-panel-loading"
      >
        Loading calling…
      </div>
    );
  }

  if (loadState === "error") {
    return (
      <div
        style={{
          position: "fixed",
          right: getSpacing("4px"),
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 1100,
          maxWidth: 280,
          backgroundColor: colorTokens["Background/White"],
          border: `1px solid ${colorTokens["Borders/Light"]}`,
          borderRadius: getSpacing("8px"),
          padding: getSpacing("12px"),
          boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
        }}
        id="ameyo-calling-panel-error"
      >
        <div
          style={{
            ...getTypography("Body/Medium"),
            marginBottom: getSpacing("8px"),
          }}
        >
          Calling toolbar unavailable
        </div>
        <div
          style={{
            ...getTypography("Body/Small"),
            color: colorTokens["Text/Light Grey"],
            marginBottom: getSpacing("8px"),
          }}
        >
          {configError}
        </div>
        <button
          type="button"
          onClick={() => void fetchConfig()}
          style={{
            ...getTypography("Links/Btn Text"),
            border: `1px solid ${colorTokens["Primary/Default"]}`,
            background: colorTokens["Background/White"],
            color: colorTokens["Primary/Default"],
            borderRadius: getSpacing("8px"),
            padding: `${getSpacing("4px")} ${getSpacing("12px")}`,
            cursor: "pointer",
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  const hasProfile = Boolean(config?.agentProfile);
  const statusLabel = !hasProfile
    ? "Not configured"
    : agentStatus?.loggedIn
      ? agentStatus.available === false
        ? "Unavailable"
        : "Ready"
      : "Logged out";

  const isLarge = open && sizeMode === "large";
  const isMinimal = open && sizeMode === "minimal";
  const showWidget = open || iframeMounted;

  const bodyWidth = isLarge
    ? Math.min(Math.floor(window.innerWidth * 0.92), LARGE_MAX_WIDTH_PX)
    : MINIMAL_WIDTH_PX;
  const bodyHeight = isLarge ? "88vh" : MINIMAL_HEIGHT_PX;

  const headerButtonStyle: React.CSSProperties = {
    ...getTypography("Links/Btn Text"),
    border: `1px solid ${colorTokens["Borders/Light"]}`,
    background: colorTokens["Background/White"],
    color: colorTokens["Text/Black"],
    borderRadius: getSpacing("8px"),
    padding: `${getSpacing("4px")} ${getSpacing("10px")}`,
    cursor: "pointer",
    whiteSpace: "nowrap",
  };

  const iconButtonStyle: React.CSSProperties = {
    ...headerButtonStyle,
    padding: `${getSpacing("4px")} ${getSpacing("8px")}`,
    minWidth: 28,
    lineHeight: 1,
  };

  /* Vertical blue pill with Wispr Flow–style waveform lines. */
  const launcher = (
    <span
      style={{
        pointerEvents: "auto",
        position: "relative",
        display: open ? "none" : "inline-flex",
      }}
    >
      <button
        type="button"
        onClick={openPanel}
        onMouseEnter={() => setLauncherTooltipOpen(true)}
        onMouseLeave={() => setLauncherTooltipOpen(false)}
        onFocus={() => setLauncherTooltipOpen(true)}
        onBlur={() => setLauncherTooltipOpen(false)}
        style={{
          display: "inline-flex",
          position: "relative",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          width: LAUNCHER_WIDTH_PX,
          margin: 0,
          padding: `${getSpacing("10px")} ${getSpacing("2px")}`,
          border: "none",
          backgroundColor: colorTokens["Primary/Default"],
          cursor: "pointer",
          borderRadius: LAUNCHER_PILL_RADIUS,
          boxShadow: "0 6px 20px rgba(5, 102, 255, 0.35)",
        }}
        aria-expanded={open}
        aria-controls="ameyo-calling-panel-body"
        aria-label={`Ameyo · ${statusLabel}`}
        aria-describedby={
          launcherTooltipOpen ? "ameyo-calling-panel-tooltip" : undefined
        }
        id="ameyo-calling-panel-toggle"
      >
        {LAUNCHER_WAVE_WIDTHS_PX.map((lineWidth, index) => (
          <span
            key={index}
            aria-hidden
            style={{
              display: "block",
              width: lineWidth,
              height: 2,
              borderRadius: 999,
              backgroundColor: colorTokens["Background/White"],
              opacity: 0.95,
              flexShrink: 0,
            }}
          />
        ))}
      </button>
      {launcherTooltipOpen && (
        <span
          id="ameyo-calling-panel-tooltip"
          role="tooltip"
          style={{
            position: "absolute",
            right: `calc(100% + ${getSpacing("8px")})`,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 1,
            padding: `${getSpacing("4px")} ${getSpacing("10px")}`,
            borderRadius: getSpacing("8px"),
            backgroundColor: colorTokens["Text/Black"],
            color: colorTokens["Background/White"],
            ...getTypography("Body/Small"),
            fontWeight: 500,
            whiteSpace: "nowrap",
            pointerEvents: "none",
            boxShadow: "0 4px 12px rgba(8, 15, 26, 0.25)",
          }}
        >
          Ameyo
        </span>
      )}
    </span>
  );

  const toolbarBody = showWidget ? (
    <div
      id="ameyo-calling-panel-body"
      style={{
        pointerEvents: open ? "auto" : "none",
        position: "relative",
        width: bodyWidth,
        height: bodyHeight,
        maxHeight: isLarge ? "88vh" : MINIMAL_HEIGHT_PX,
        backgroundColor: colorTokens["Background/White"],
        border: `1px solid ${colorTokens["Borders/Light"]}`,
        borderRadius: getSpacing("8px"),
        boxShadow: isLarge
          ? "0 16px 48px rgba(0,0,0,0.2)"
          : "0 4px 20px rgba(8, 15, 26, 0.1)",
        display: open ? "flex" : "none",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: `${getSpacing("10px")} ${getSpacing("12px")}`,
          borderBottom: `1px solid ${colorTokens["Borders/Light"]}`,
          backgroundColor: colorTokens["Background/Greyish"],
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: getSpacing("8px"),
          flexShrink: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            minWidth: 0,
          }}
        >
          <span style={{ ...getTypography("Body/Medium"), fontWeight: 600 }}>
            Agent Login
          </span>
          <span
            style={{
              ...getTypography("Body/Small"),
              color: colorTokens["Text/Light Grey"],
            }}
          >
            {statusLabel}
            {config?.agentProfile?.ameyoAgentId
              ? ` · ${config.agentProfile.ameyoAgentId}`
              : ""}
          </span>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: getSpacing("8px"),
            flexShrink: 0,
            alignItems: "center",
          }}
        >
          {isLarge ? (
            <button
              type="button"
              id="ameyo-calling-panel-dock"
              onClick={() => setSizeMode("minimal")}
              style={headerButtonStyle}
            >
              Minimize
            </button>
          ) : (
            <button
              type="button"
              id="ameyo-calling-panel-enlarge"
              onClick={() => setSizeMode("large")}
              style={headerButtonStyle}
            >
              Expand
            </button>
          )}
          <button
            type="button"
            id="ameyo-calling-panel-close"
            onClick={minimizePanel}
            style={iconButtonStyle}
            aria-label="Minimize calling panel"
            title="Minimize"
          >
            −
          </button>
        </div>
      </div>

      {!hasProfile ? (
        <div
          style={{
            padding: getSpacing("16px"),
            ...getTypography("Body/Small"),
            color: colorTokens["Text/Black"],
          }}
          id="ameyo-calling-panel-no-profile"
        >
          Contact admin to configure your calling profile.
        </div>
      ) : (
        <>
          {!agentStatus?.loggedIn && isMinimal && (
            <div
              style={{
                margin: getSpacing("12px"),
                marginBottom: 0,
                padding: getSpacing("8px"),
                backgroundColor: colorTokens["Background/Greyish"],
                borderRadius: getSpacing("8px"),
                ...getTypography("Body/Small"),
                flexShrink: 0,
              }}
              id="ameyo-calling-panel-login-hint"
            >
              Log in with your Ameyo ID and password below. Use Expand if the
              form feels cramped.
            </div>
          )}
          <iframe
            id="ameyo-toolbar"
            title="Ameyo calling toolbar"
            src={config?.iframeUrl}
            allow="microphone"
            style={{
              border: "none",
              width: "100%",
              flex: 1,
              minHeight: 0,
              backgroundColor: colorTokens["Background/White"],
            }}
            onError={() => {
              toast.error("Failed to load Ameyo toolbar");
            }}
          />
        </>
      )}
    </div>
  ) : null;

  return (
    <div
      style={{
        position: "fixed",
        zIndex: 1100,
        ...(isLarge
          ? {
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "auto",
              backgroundColor: "rgba(15, 23, 42, 0.45)",
            }
          : {
              right: getSpacing("4px"),
              top: "50%",
              transform: "translateY(-50%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              justifyContent: "center",
              gap: isMinimal ? getSpacing("12px") : 0,
              pointerEvents: "none",
            }),
      }}
      id="ameyo-calling-panel"
      onClick={
        isLarge
          ? (event) => {
              if (event.target === event.currentTarget) {
                setSizeMode("minimal");
              }
            }
          : undefined
      }
    >
      {launcher}
      {toolbarBody}
    </div>
  );
};

export default AmeyoCallingPanel;
